<script lang="ts">
    import { computed, onBeforeUnmount, ref } from "vue";
    import emitter from "tiny-emitter/instance";
    import { openDialog } from "@/utils";
    import tabIcon from "@/assets/besokslista.svg";
    import KHeader from "@/components/KHeader.vue";
    import KButton from "@/components/Button.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { KTableAction, TableValue } from "@/components/KTable/types";
    import BesokslistaFilter from "@/tabs/Besok/Besokslista/BesokslistaFilter.vue";
    import BokaDirektKDialog from "@/tabs/Besok/BokaDirektKDialog.vue";
    import { Column } from "@/components/KTable/types";
    import CellBesokStatus from "@/tabs/Besok/Besokslista/CellBesokStatus.vue";
    import { CellContentAlignment, CellContentWidth } from "@/components/KTable/helpers";
    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";
    import { Oppenvardsbesok } from "@/tabs/Oppenvard/types";
    import PlusWhiteNoFillIcon from "@/assets/plus_circle_white_nofill.svg";
    import { getHourMinuteFromTime } from "@/tabs/Operation/utils";
    import ActionIconEdit from "@/assets/actionIcons/edit.svg";
    import ActionIconBokaoperation from "@/assets/actionIcons/bokaoperation.svg";
    import ActionIconAvboka from "@/assets/actionIcons/avboka.svg";
    import BesoksunderlagSkapaRedigera from "@/tabs/Besok/BesoksunderlagSkapaRedigera/BesoksunderlagSkapaRedigera.vue";

    export default {
        name: "Besokslista",
        tabName: "Besökslista",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    const props = defineProps<{
        tabId: number;
        initialQueryParams?: Record<string, unknown>;
    }>();

    const store = useStore();

    const besokslistaTableName = "besokslista-table";
    const besokslistaFilterName = "besokslista";
    const besokslistaFilterFormId = `${besokslistaFilterName}-filter-form`;

    const { formApi } = useKForm({ id: besokslistaFilterFormId });

    const today = new Date().toLocaleDateString("sv-SE");

    const queryParams = ref<{ [key: string]: string | number | boolean | Array<string | number> }>({
        show_avbokade_strukna: false,
        bokat_datum__gte: today,
        bokat_datum__lte: today,

        ...props.initialQueryParams,
    });

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const subsetName = ref<string>("default");

    const columns: Column[] = [
        {
            header: "PersonID",
            field: "personId",
            sortField: "patient__personId",
            frozen: true,
            alignFrozen: "left",
            limitWidth: true,
            minWidth: CellContentWidth.custom({ amount: 8 }),
            alignContent: CellContentAlignment.Center,
            sortable: true,
        },
        {
            header: "Patientnamn",
            field: "patientnamn",
            sortField: "patient__namn",
            sortable: true,
        },
        {
            header: "Personal",
            field: "vardpersonalNamn",
            sortField: "vardpersonal__namn",
            sortable: true,
            contentType: "array",
            contentDirection: "vertical",
        },
        {
            header: "Roll",
            field: "roller",
            sortField: "besoksunderlag__mall__roller__displayName",
            sortable: true,
            contentType: "array",
            contentDirection: "vertical",
        },
        {
            header: "Form",
            field: "form",
            sortField: "besoksunderlag__mall__typ__form",
            sortable: true,
        },
        {
            header: "Besökstyp",
            field: "besokstyp",
            sortField: "besoksunderlag__mall__typ__namn",
            sortable: true,
        },
        {
            header: "Besökslängd",
            field: "besokslangd",
            sortField: "besokslangd",
            sortable: true,
        },
        {
            header: "Enhet",
            field: "enhet",
            sortField: "besoksunderlag__enhet__alias",
            sortable: true,
        },
        {
            header: "Avtal",
            field: "avtal",
            sortField: "besoksunderlag__avtal__namn",
            sortable: true,
        },
        {
            header: "Datum",
            field: "bokatDatum",
            sortField: "bokat_datum",
            sortable: true,
        },
        {
            header: "Tid",
            field: "bokadTid",
            sortField: "bokad_tid",
            sortable: true,
        },
        {
            field: "status",
            header: "Status",
            sortField: "status",
            frozen: true,
            alignFrozen: "right",
            bodyTemplate: CellBesokStatus,
            limitWidth: true,
            minWidth: CellContentWidth.s,
            sortable: true,
        },
    ];

    const dropdownActions: KTableAction<BesokRowData>[] = [
        {
            title: "Omboka besök",
            icon: ActionIconBokaoperation,
            show: (item) => {
                return false;
            },
        },
        {
            title: "Hantera besök	",
            icon: ActionIconBokaoperation,
            show: (item) => {
                return false;
            },
        },
        {
            title: "Avboka besök",
            icon: ActionIconAvboka,
            show: (item) => {
                return false;
            },
        },

        {
            title: "Boka återbesök",
            icon: ActionIconBokaoperation,
            show: (item) => {
                return false;
            },
        },
        {
            title: "Redigera besöksunderlag",
            icon: ActionIconBokaoperation,
            show: (item) => false,
            callback: ({ data }: { data: BesokRowData }) => {
                openEditbesoksunderlag({ data });
            },
        },
    ];
    ActionIconEdit;
    ActionIconBokaoperation;
    ActionIconAvboka;

    type BesokRowData = {
        id: string;
        personId?: string;
        patientnamn?: string;
        patientId?: number;
        vardpersonalNamn?: string[] | undefined;
        roller?: string[] | undefined;
        form?: string;
        besokstyp?: string;
        besokslangd?: string | null;
        enhet?: string;
        avtal?: string;
        bokatDatum?: string | null;
        bokadTid?: string | null;
        status?: Oppenvardsbesok["status"];
        besoksunderlag: string;
        besoksunderlagStatus?: Oppenvardsbesok["besoksunderlag_display"]["status"];
    };

    const oppenvardsbesok = computed<BesokRowData[]>(() =>
        store.getters["oppenvardsbesok/oppenvardsbesokSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        }).map((besok: Oppenvardsbesok) => {
            if (!besok.besoksunderlag_display) {
                return {} as BesokRowData;
            }
            const vardpersonal = besok.besoksunderlag_display.vardpersonal_display;

            return {
                id: besok.id,
                personId: besok.besoksunderlag_display.patient_display?.personId_formatted,
                patientnamn: besok.besoksunderlag_display.patient_display?.namn_reverse,
                patientId: besok.besoksunderlag_display.patient_display?.id,
                vardpersonalNamn: vardpersonal?.map((vp) => vp.user_display?.name),
                roller: vardpersonal?.map((vp) => vp?.roll_display?.displayName),
                form: besok.besoksunderlag_display.mall_display?.typ_display?.form,
                besokstyp: besok.besoksunderlag_display.mall_display?.typ_display?.namn,
                besokslangd: besok.besokslangd,
                enhet: besok.besoksunderlag_display.enhet_display?.name,
                avtal: besok.besoksunderlag_display.avtal_display?.namn,
                bokatDatum: besok.bokat_datum,
                bokadTid: getHourMinuteFromTime(besok.bokad_tid ?? ""),
                status: besok.status,
                besoksunderlag: besok.besoksunderlag,
                besoksunderlagStatus: besok.besoksunderlag_display.status,
            } satisfies BesokRowData;
        })
    );

    const isLoading = ref(false);
    const bokaDirektDialogRef = ref();
    const besokslistaFilterRef = ref();

    const first = computed<number>(() => {
        return (listPage.value - 1) * listLimit.value;
    });

    const onRowClick = ({ data }: { data: TableValue }) => {
        if (data.patientId) {
            store.dispatch("patientData/loadFromBackend", {
                id: data.patientId,
                failSilently: false,
            });
        }
    };

    const onPage = async ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        await fetchOppenvardsbesok({ page, limit: rows });
    };

    const onVisaStrukna = async ({ value }: { value: boolean }) => {
        formApi.value?.setFieldValue?.({
            value,
            name: "show_avbokade_strukna",
        });

        const data = await formApi.value?.manualSubmit?.();

        if (data) {
            onFilter({ value: data });
        }
    };

    const patient = computed(() => {
        return store.getters["patientData/patient"];
    });

    const openEditbesoksunderlag = ({ data }: { data: BesokRowData }) => {
        store.dispatch("tabs/openTab", {
            component: BesoksunderlagSkapaRedigera,
            parameters: {
                besoksunderlagId: data.besoksunderlag,
            },
        });
    };

    const openDirektboka = () => {
        bokaDirektDialogRef.value?.show();
    };

    const fetchOppenvardsbesok = async ({
        page = 1,
        limit = listLimit.value,
        resetLocalSubsets,
    }: { page?: number; limit?: number; resetLocalSubsets?: boolean } = {}) => {
        isLoading.value = true;

        try {
            const { total, subsetName: fetchedSubsetName } = await store.dispatch(
                "oppenvardsbesok/fetchOppenvardsbesok",
                {
                    filterParams: queryParams.value,
                    page,
                    limit,
                    resetLocalSubsets,
                }
            );
            isLoading.value = false;
            subsetName.value = fetchedSubsetName;
            listCount.value = total || 0;
            listPage.value = page;
        } catch (error) {
            isLoading.value = false;
            openDialog("Kunde inte hämta besökslista", "error");
        }
    };

    const applyQueryParams = ({ value }: { value: any }) => {
        queryParams.value = value;
        fetchOppenvardsbesok({ page: 1 });
    };

    const onFilter = async ({ value }: { value: any }) => {
        applyQueryParams({ value });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        // "ordering" skapas här i formulärdatan (om ej redan existerar) genom "setFieldValue" i KForm
        if (sortField) {
            formApi.value?.setFieldValue?.({
                name: "ordering",
                value: `${sortOrder > 0 ? "" : "-"}${sortField}`,
            });
        } else {
            formApi.value?.setFieldValue?.({ name: "ordering", value: null });
        }
        const data = await formApi.value?.manualSubmit?.();

        if (data) {
            applyQueryParams({ value: data });
        }
    };

    const onRefreshTab = async () => {
        queryParams.value = await formApi.value?.manualSubmit?.();
        await fetchOppenvardsbesok({ page: 1, resetLocalSubsets: true });
    };

    async function init() {
        await store.dispatch("oppenvardsbesok/addSubscriber");
        await fetchOppenvardsbesok();

        // TODO emitters ska fasas ut med tiden
        emitter.on(`refreshTab-${props.tabId}`, onRefreshTab);
    }

    const onSaved = () => {
        besokslistaFilterRef?.value?.onClearAll();
    };

    init();

    onBeforeUnmount(async () => {
        await store.dispatch("oppenvardsbesok/removeSubscriber");
        emitter.off(`refreshTab-${props.tabId}`, onRefreshTab);
    });
</script>

<template>
    <div>
        <KHeader heading="Besökslista">
            <KButton type="button" :disabled="!patient?.id" size="small" @click="openDirektboka">
                <img :src="PlusWhiteNoFillIcon" />
                Direktboka
            </KButton>
        </KHeader>
        <BesokslistaFilter
            ref="besokslistaFilterRef"
            :name="besokslistaFilterName"
            :keepOnClear="['show_avbokade_strukna', 'ordering']"
            :tableStateName="besokslistaTableName"
            :defaultValue="queryParams"
            @submit="onFilter"
        />
        <KTable
            reorderableColumns
            togglableColumns
            paginator
            alwaysShowPaginator
            useState
            loadingMessage="Hämtar besökslista..."
            paginatorPosition="bottom"
            :name="besokslistaTableName"
            :columns="columns"
            :actions="dropdownActions"
            :value="oppenvardsbesok"
            :totalRecords="listCount"
            :loading="isLoading"
            :unselectable="true"
            :first="first"
            @rowClick="onRowClick"
            @menuClick="onRowClick"
            @page="onPage"
            @sort="onSort"
        >
            <template #header>
                <KCheckbox
                    name="show_avbokade_strukna"
                    label="Visa avbokade och strukna"
                    valueType="boolean"
                    :value="initialQueryParams?.show_avbokade_strukna ?? false"
                    @inputValue="onVisaStrukna"
                />
            </template>
        </KTable>
        <BokaDirektKDialog ref="bokaDirektDialogRef" :patient="patient" @saved="onSaved" />
    </div>
</template>
<style lang="scss" scoped>
    ::v-deep {
        .k-table:not(.empty) .p-datatable {
            height: 40rem;
        }
        .k-table.empty .p-datatable {
            height: 20rem;
        }
    }
</style>
